import type { UniqueIdentifier } from '@dnd-kit/core';
import {
  DndContext,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useCallback, useState } from 'react';

import type { ApiFilestackImageOrVideoDataWithCaption } from '#/api/types/ApiFilestackImageData';
import { Box } from '#/components/Box/Box';

import { SortableImage } from './SortableImage';

type Props = {
  assets: ApiFilestackImageOrVideoDataWithCaption[];
  onSortEnd: (arg: { oldIndex: number; newIndex: number }) => void;
  removeImage: (index: number) => void;
  editImage: (index: number) => void;
  qaId: string;
  children: React.ReactNode;
};

export function SortableGrid({
  assets,
  onSortEnd,
  removeImage,
  editImage,
  qaId,
  children,
}: Props) {
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);

  // Returns an asset ID wether or not it is persisted
  const getId = useCallback(
    (asset: ApiFilestackImageOrVideoDataWithCaption) =>
      asset.type === 'VIDEO' ? asset.videoUrl : asset.handle,
    [],
  );

  const getIndex = (id: UniqueIdentifier) =>
    assets.findIndex((asset) => getId(asset) === id);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
  );

  return (
    <DndContext
      sensors={sensors}
      autoScroll={false}
      onDragStart={({ active }) => {
        if (active) {
          setActiveId(active.id);
        }
      }}
      onDragEnd={({ active, over }) => {
        if (over && active.id !== over.id) {
          onSortEnd({
            oldIndex: getIndex(active.id),
            newIndex: getIndex(over.id),
          });
        }
        setActiveId(null);
      }}
      onDragCancel={() => setActiveId(null)}
    >
      <SortableContext
        items={assets.map((asset) => ({ ...asset, id: getId(asset) }))}
        strategy={verticalListSortingStrategy}
      >
        <Box
          display="flex"
          flexDirection={['column', 'row', null, null]}
          flexWrap={['nowrap', 'wrap', null, null]}
          m={-11}
        >
          {assets.map((asset, index) => (
            <SortableImage
              key={getId(asset)}
              id={getId(asset)}
              activeId={activeId}
              image={asset}
              removeImage={() => removeImage(index)}
              editImage={() => editImage(index)}
              qaId={qaId}
            />
          ))}
          {children}
        </Box>
      </SortableContext>
    </DndContext>
  );
}
